import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { modelPool } from "@modules/model/ModelPool";
import "./ModelTab.component.css";
import { ModelPropertiesView } from "./model-tab-content/ModelPropertiesView.component";
import { ModelTreeView } from "./model-tab-content/ModelTreeView.component";
import { buildModelTree, cloneModelTree, HIGHLIGHT_TIMEOUT_MS, } from "./modelTreeUtils";
import { filterTree } from "./searchUtils";
export function ModelTab() {
    var _a, _b;
    const [autoUpdate, setAutoUpdate] = useState(true);
    const [tree, setTree] = useState(undefined);
    const [selectedUID, setSelectedUID] = useState(null);
    const [search, setSearch] = useState("");
    const [contextMenu, setContextMenu] = useState(null);
    const expandedMap = useRef(new Map());
    const dirtyRef = useRef(false);
    /// Manage model tree ///
    const createTreeLogic = (prev) => {
        const root = modelPool.getRoot();
        const now = Date.now();
        const nextTree = buildModelTree(root, prev, now, autoUpdate);
        if (selectedUID !== null && !nextTree.nodeMap.has(selectedUID)) {
            setSelectedUID(null);
        }
        return nextTree;
    };
    const createTree = () => {
        setTree(createTreeLogic);
    };
    /// Expand tree logic ///
    const toggleExpanded = (uid) => {
        var _a;
        expandedMap.current.set(uid, !((_a = expandedMap.current.get(uid)) !== null && _a !== void 0 ? _a : false));
        setTree((prev) => {
            if (prev === undefined)
                return prev;
            const curTree = cloneModelTree(prev);
            const expandRecursive = (node) => {
                var _a;
                node.expanded = (_a = expandedMap.current.get(node.uid)) !== null && _a !== void 0 ? _a : false;
                node.childNodes.forEach(expandRecursive);
            };
            if (curTree.root !== undefined)
                expandRecursive(curTree.root);
            return curTree;
        });
    };
    /// Initial load ///
    useEffect(() => {
        createTree();
    }, []);
    useEffect(() => {
        if (contextMenu === null)
            return;
        const closeMenu = () => {
            setContextMenu(null);
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                closeMenu();
            }
        };
        window.addEventListener("click", closeMenu);
        window.addEventListener("contextmenu", closeMenu);
        window.addEventListener("scroll", closeMenu, true);
        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("click", closeMenu);
            window.removeEventListener("contextmenu", closeMenu);
            window.removeEventListener("scroll", closeMenu, true);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [contextMenu]);
    /// Auto update ///
    // Register changed handlers
    useEffect(() => {
        if (!autoUpdate)
            return;
        const interval = setInterval(() => {
            if (!dirtyRef.current)
                return;
            dirtyRef.current = false;
            createTree();
        }, 16); // ~60fps
        return () => {
            clearInterval(interval);
        };
    }, [autoUpdate]);
    useEffect(() => {
        if (!autoUpdate)
            return;
        if (tree === undefined)
            return;
        const timeoutMs = getNextTreeHighlightTimeout(tree);
        if (timeoutMs === undefined)
            return;
        const timeout = window.setTimeout(() => {
            dirtyRef.current = true;
        }, timeoutMs);
        return () => {
            window.clearTimeout(timeout);
        };
    }, [autoUpdate, tree]);
    useEffect(() => {
        if (!autoUpdate)
            return;
        const unsubscribers = [];
        unsubscribers.push(modelPool.setOnModelCreatedHandler((_model) => {
            dirtyRef.current = true;
        }));
        unsubscribers.push(modelPool.setOnModelDestroyedHandler((_model) => {
            dirtyRef.current = true;
        }));
        const attachModelHandlers = (model) => {
            unsubscribers.push(model.setOnNameChangedHandler((_m) => {
                dirtyRef.current = true;
            }));
            unsubscribers.push(model.setOnRoomChangedHandler((_m, _room) => {
                dirtyRef.current = true;
            }));
            unsubscribers.push(model.setOnParentChangedHandler((_m, _room) => {
                dirtyRef.current = true;
            }));
            unsubscribers.push(model.setOnChildAddedHandler((_m, _child) => {
                dirtyRef.current = true;
            }));
            unsubscribers.push(model.setOnChildRemovedHandler((_m, _child) => {
                dirtyRef.current = true;
            }));
            unsubscribers.push(model.setOnFacetAddedHandler((_m, _facet, _created) => {
                dirtyRef.current = true;
            }));
            unsubscribers.push(model.setOnTransformChangedHandler((_m, _transform) => {
                dirtyRef.current = true;
            }));
            model.getChildren().forEach(attachModelHandlers);
        };
        attachModelHandlers(modelPool.getRoot());
        return () => {
            unsubscribers.forEach((u) => {
                u();
            });
        };
    }, [autoUpdate]);
    /// Render ///
    const deleteModel = (uid) => {
        const model = modelPool.getModel(uid);
        if (model === undefined || model.isRoot())
            return;
        const parent = model.getParent();
        if (parent === undefined)
            return;
        const children = [...model.getChildren()];
        parent.addChildren(children);
        modelPool.destroyModel(model, { destroyPermanently: true });
        if (selectedUID === uid) {
            setSelectedUID(parent.getUID());
        }
        createTree();
    };
    const deleteHierarchy = (uid) => {
        var _a, _b;
        const model = modelPool.getModel(uid);
        if (model === undefined || model.isRoot())
            return;
        const parentUID = (_b = (_a = model.getParent()) === null || _a === void 0 ? void 0 : _a.getUID()) !== null && _b !== void 0 ? _b : null;
        modelPool.destroyModel(model, { handleChildren: "destroyRecursive", destroyPermanently: true });
        if (selectedUID === uid) {
            setSelectedUID(parentUID);
        }
        createTree();
    };
    const openContextMenu = (uid, x, y) => {
        setSelectedUID(uid);
        setContextMenu({ uid, x, y });
    };
    const logModelToConsole = (uid) => {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        console.log(model);
    };
    const toggleVisibility = (uid) => {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        model.setVisible(!model.isVisible());
    };
    const selectedNode = selectedUID !== null ? tree === null || tree === void 0 ? void 0 : tree.nodeMap.get(selectedUID) : undefined;
    const contextMenuNode = contextMenu !== null ? tree === null || tree === void 0 ? void 0 : tree.nodeMap.get(contextMenu.uid) : undefined;
    const contextMenuDisabled = contextMenuNode === undefined || contextMenuNode.uid === ((_a = tree === null || tree === void 0 ? void 0 : tree.root) === null || _a === void 0 ? void 0 : _a.uid);
    const filteredRoot = (tree === null || tree === void 0 ? void 0 : tree.root) && search.trim().length > 0
        ? filterTree(tree.root, search)
        : tree === null || tree === void 0 ? void 0 : tree.root;
    return (React.createElement("div", { className: "model-tab-container" },
        React.createElement("div", { className: "model-tab-header" },
            React.createElement("input", { type: "text", placeholder: "Search by name or UID...", value: search, onChange: (e) => { setSearch(e.target.value); } }),
            React.createElement("button", { disabled: autoUpdate, onClick: () => {
                    createTree();
                } }, "Update"),
            React.createElement("label", { className: "model-tab-checkbox" },
                React.createElement("input", { type: "checkbox", checked: autoUpdate, onChange: (e) => {
                        const enabled = e.target.checked;
                        setAutoUpdate(enabled);
                        if (enabled) {
                            createTree();
                        }
                        else {
                            dirtyRef.current = false;
                        }
                    } }),
                "Auto update")),
        React.createElement("div", { className: "model-tab-body" },
            React.createElement("div", { className: "model-tree" }, tree !== null && (React.createElement(ModelTreeView, { node: filteredRoot, depth: 0, onToggle: toggleExpanded, onSelect: setSelectedUID, selectedUID: selectedUID, autoUpdate: autoUpdate, search: search, onContextMenu: openContextMenu }))),
            React.createElement("div", { className: "model-properties" },
                React.createElement(ModelPropertiesView, { model: selectedNode, autoUpdate: autoUpdate, onModelChanged: createTree }))),
        contextMenu !== null && (React.createElement("div", { className: "model-tab-context-menu", style: { left: contextMenu.x, top: contextMenu.y }, onClick: (event) => {
                event.stopPropagation();
            }, onContextMenu: (event) => {
                event.preventDefault();
                event.stopPropagation();
            } },
            React.createElement("button", { onClick: () => {
                    logModelToConsole(contextMenu.uid);
                    setContextMenu(null);
                } }, "Log to Console"),
            React.createElement("button", { onClick: () => {
                    toggleVisibility(contextMenu.uid);
                    setContextMenu(null);
                } }, ((_b = modelPool.getModel(contextMenu.uid)) === null || _b === void 0 ? void 0 : _b.isVisible()) ? "Hide" : "Show"),
            React.createElement("button", { disabled: contextMenuDisabled, onClick: () => {
                    deleteModel(contextMenu.uid);
                    setContextMenu(null);
                } }, "Delete"),
            React.createElement("button", { disabled: contextMenuDisabled, onClick: () => {
                    deleteHierarchy(contextMenu.uid);
                    setContextMenu(null);
                } }, "Delete Hierarchy")))));
}
const getNextTreeHighlightTimeout = (tree) => {
    let nextTimeout = Infinity;
    const now = Date.now();
    const visitNode = (node) => {
        if (node === undefined)
            return;
        if (node.changeType !== "unchanged" && node.changedAt !== undefined) {
            const remaining = HIGHLIGHT_TIMEOUT_MS - (now - node.changedAt);
            if (remaining > 0) {
                nextTimeout = Math.min(nextTimeout, remaining);
            }
        }
        for (const child of node.childNodes) {
            visitNode(child);
        }
    };
    visitNode(tree.root);
    for (const deletedNode of tree.deletedModels.values()) {
        visitNode(deletedNode);
    }
    return Number.isFinite(nextTimeout) ? Math.max(0, nextTimeout) : undefined;
};
